import React, { useState, useEffect, useRef } from "react";
import * as Mui from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "redux/store";
import { Agent, AgentPublicBio } from "model/agent";
import { MarketResource } from "api/resource/market";
import { Upload } from "model/upload";
import { RichTextEditor } from "component/shared/rich-text-editor";
import { updateAgent, createAgentTestimonial, updateAgentTestimonial, deleteAgentTestimonial, fetchAgentTestimonials } from "redux/slice/agents";
import { SaveButton } from "component/shared/save-button";
import { getMarkets } from "api/api";
import { validate, ValidationErrors } from "./validation";
import {
	HeaderSection,
	SocialMediaLinksSection,
	AboutSection,
	MarketsSection,
	TestimonialsSection,
	TestimonialModal,
} from "./components";
import * as env from "shared/env";

interface Props {
	agent: Agent;
	isEnhancedBioEnabled?: boolean;
	// When the detail-view refresh hosts this tab, it supplies a single shared save bar, so hide
	// BioTab's own and let the parent trigger the bio save via saveRef. dirtyRef exposes whether
	// the bio fields have unsaved edits, and onDirtyChange notifies the host when they change — so
	// the shared "unsaved changes" indicator reflects bio edits too.
	hideSaveBar?: boolean;
	saveRef?: React.MutableRefObject<(() => Promise<void>) | undefined>;
	// Lets the host freeze this tab's testimonial state before it issues its OWN agent PUT.
	// That PUT's response echoes the stored testimonials, and without freezing first the echo
	// lands before our save begins and wipes the pending edits (MAX-4319).
	beginSaveRef?: React.MutableRefObject<(() => void) | undefined>;
	// Releases that freeze if the host's own PUT fails and this tab's save never runs.
	abortSaveRef?: React.MutableRefObject<(() => void) | undefined>;
	dirtyRef?: React.MutableRefObject<(() => boolean) | undefined>;
	/**
	 * Exposes this tab's reset to the host. With hideSaveBar the built-in Cancel button is gone, so
	 * without this the bio fields have no way to be reverted (MAX-4139).
	 */
	cancelRef?: React.MutableRefObject<(() => void) | undefined>;
	onDirtyChange?: () => void;
}

interface LegacyState {
	head: string;
	body: string;
}

export const BioTab: React.FC<Props> = ({ agent, isEnhancedBioEnabled = false, hideSaveBar = false, saveRef, beginSaveRef, abortSaveRef, dirtyRef, cancelRef, onDirtyChange }) => {
	const dispatch = useDispatch<any>();
	const loading = useSelector((state: RootState) => state.agents.loading);

	// Legacy state (for when enhanced is disabled)
	const [legacyState, setLegacyState] = useState<LegacyState>({
		head: agent.head || "",
		body: agent.body || "",
	});

	// Enhanced state
	const [publicBio, setPublicBio] = useState<AgentPublicBio>({
		showHeader: false,
		pageHeaderText: null,
		heroImage: null,
		quickSearch: false,
		logoImage: null,
		linkedinUrl: '',
		facebookUrl: '',
		instagramUrl: '',
		twitterUrl: '',
		tiktokUrl: '',
		aboutBody: null,
		markets: [],
		testimonials: [],
	});

	const [errors, setErrors] = useState<ValidationErrors>({});
	const [markets, setMarkets] = useState<MarketResource[]>([]);
	const [saveError, setSaveError] = useState<string | null>(null);

	const [saveSuccess, setSaveSuccess] = useState(false);
	const [testimonialModalOpen, setTestimonialModalOpen] = useState(false);
	const [editingTestimonialIndex, setEditingTestimonialIndex] = useState<number | null>(null);
	const [testimonialFormData, setTestimonialFormData] = useState({ quote: '', author: '' });
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [testimonialToDelete, setTestimonialToDelete] = useState<number | null>(null);

	useEffect(() => {
		if (isEnhancedBioEnabled) {
			getMarkets().then((marketList) => {
				setMarkets(marketList);
			}).catch(() => {
				// Failed to load markets - continue with empty list
			});
		}
	}, [isEnhancedBioEnabled]);


	// Which agent the enhanced state below was hydrated from. Saving dispatches agentLoaded, which
	// replaces the cached agent with a new object reference; without this guard the effect below
	// would re-run on that echo and reset the user's unsaved edits (MAX-4311).
	const hydratedAgentIdRef = useRef<number | null>(null);

	useEffect(() => {
		if (isEnhancedBioEnabled && agent && hydratedAgentIdRef.current !== agent.id) {
			hydratedAgentIdRef.current = agent.id;
			const initialPublicBio: AgentPublicBio = {
				showHeader: agent.publicBio?.showHeader ?? false,
				pageHeaderText: agent.publicBio?.pageHeaderText ?? (agent.head || null),
				heroImage: agent.publicBio?.heroImage ?? null,
				quickSearch: agent.publicBio?.quickSearch ?? false,
				logoImage: agent.publicBio?.logoImage ?? null,
				linkedinUrl: agent.publicBio?.linkedinUrl ?? '',
				facebookUrl: agent.publicBio?.facebookUrl ?? '',
				instagramUrl: agent.publicBio?.instagramUrl ?? '',
				twitterUrl: agent.publicBio?.twitterUrl ?? '',
				tiktokUrl: agent.publicBio?.tiktokUrl ?? '',
				aboutBody: agent.publicBio?.aboutBody ?? (agent.body || null),
				markets: agent.publicBio?.markets ?? [],
				testimonials: agent.publicBio?.testimonials ?? [],
			};
			setPublicBio(initialPublicBio);
		}
	}, [agent, isEnhancedBioEnabled]);

	// Testimonials are saved through their own endpoints, which refetch and push server-assigned ids
	// back into the cached agent. The hydrate guard above deliberately ignores that echo, so mirror
	// just this one field through — without it a newly created testimonial would keep a local
	// undefined id and be created a second time on the next save (MAX-4311).
	//
	// MAX-4319: it must not run mid-save. WebsiteTab saves in two steps and ihf-root's agent PUT
	// always echoes the stored testimonials, so mirroring that echo overwrote pending edits and
	// the diff below then found nothing to sync — a 200 with nothing saved. The save adopts the
	// server's final list itself, since this effect is suppressed while it runs.
	const serverTestimonials = agent?.publicBio?.testimonials;
	const savingRef = useRef(false);
	// The live edit buffer and the last server-known testimonial list, held in refs so a
	// mid-save re-render cannot substitute stale values (MAX-4319).
	const publicBioRef = useRef(publicBio);
	publicBioRef.current = publicBio;
	const originalTestimonialsRef = useRef(serverTestimonials ?? []);
	if (!savingRef.current) {
		originalTestimonialsRef.current = serverTestimonials ?? [];
	}
	useEffect(() => {
		if (isEnhancedBioEnabled && serverTestimonials && !savingRef.current) {
			setPublicBio((prev) =>
				JSON.stringify(prev.testimonials ?? []) === JSON.stringify(serverTestimonials)
					? prev
					: { ...prev, testimonials: serverTestimonials }
			);
		}
	}, [serverTestimonials, isEnhancedBioEnabled]);

	const updatePublicBioField = <K extends keyof AgentPublicBio>(
		field: K,
		value: AgentPublicBio[K]
	) => {
		setPublicBio((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => {
				const newErrors = { ...prev };
				delete newErrors[field];
				return newErrors;
			});
		}
	};


	const handleSaveLegacy = async () => {
		const payload: Agent = {
			...agent,
			head: legacyState.head,
			body: legacyState.body,
		};
		await dispatch(updateAgent({ agent: payload }));
	};

	const handleSaveEnhanced = async () => {
		const validationErrors = validate(publicBio);
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			setSaveError("Please fix the errors before saving.");
			setTimeout(() => {
				const firstErrorElement = document.querySelector('[data-error="true"]');
				firstErrorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}, 100);
			// Must reject, not return: a host save bar would otherwise report success for a save
			// that never happened. Release the freeze the host may have armed before its own PUT.
			savingRef.current = false;
			throw new Error("Bio validation failed");
		}

		setSaveError(null);

		// MAX-4319: read both sides of the diff from refs, never from props/state. WebsiteTab saves
		// in two steps, and updateAgent dispatches agentLoaded from an un-awaited .then(); that echo
		// re-renders this component mid-save and reassigns saveRef.current to a fresh closure built
		// over the echoed, pre-edit agent. So whichever closure ends up running, it must not read the
		// user's edits out of its own captured scope.
		savingRef.current = true;
		const { testimonials: currentTestimonials = [], ...publicBioWithoutTestimonials } =
			publicBioRef.current;
		const originalTestimonials = originalTestimonialsRef.current;

		try {
			// Save agent — testimonials excluded, they go through dedicated endpoints
			const payload: Agent = { ...agent, publicBio: publicBioWithoutTestimonials };
			try {
				await dispatch(updateAgent({ agent: payload })).unwrap();
			} catch(error) {
				setSaveError("Couldn't save. Please try again.");
				throw error;
			}

			// Sync testimonials via dedicated endpoints — each thunk refetches after its operation
			try {
				// Delete testimonials that were removed
				const currentIds = new Set(currentTestimonials.filter(t => t.id).map(t => t.id!));
				for (const orig of originalTestimonials) {
					if (orig.id && !currentIds.has(orig.id)) {
						await dispatch(deleteAgentTestimonial({ agentId: agent.id, testimonialId: orig.id })).unwrap();
					}
				}

				// Create new testimonials (no id) and update changed ones (have id)
				for (const t of currentTestimonials) {
					if (!t.id) {
						await dispatch(createAgentTestimonial({ agentId: agent.id, testimonial: { quote: t.quote, author: t.author } })).unwrap();
					} else {
						const orig = originalTestimonials.find(o => o.id === t.id);
						if (orig && (orig.quote !== t.quote || orig.author !== t.author)) {
							await dispatch(updateAgentTestimonial({ agentId: agent.id, testimonialId: t.id, testimonial: { quote: t.quote, author: t.author } })).unwrap();
						}
					}
				}
				// Adopt the server's post-save list explicitly. The mirror effect is suppressed for
				// the duration of the save, so it would otherwise skip this final echo and leave a
				// newly created testimonial without its server-assigned id.
				const synced = await dispatch(fetchAgentTestimonials({ agentId: agent.id })).unwrap();
				if (synced) {
					setPublicBio((prev) => ({ ...prev, testimonials: synced }));
				}
				// Success only once testimonials actually synced (MAX-4319).
				setSaveSuccess(true);
			} catch(error) {
				setSaveError("Agent saved but testimonials couldn't be synced. Please try again.");
				throw error;
			}
		} finally {
			savingRef.current = false;
		}
	};

	// handleSaveEnhanced rethrows so a host save bar can report the failure. This tab's own button
	// has no host to report to — saveError is already set — so it absorbs the rejection here.
	const handleSaveOwnButton = async () => {
		try {
			await handleSaveEnhanced();
		} catch(error) {
			// Already surfaced via saveError.
		}
	};

	if (saveRef) {
		saveRef.current = isEnhancedBioEnabled ? handleSaveEnhanced : handleSaveLegacy;
	}

	if (beginSaveRef) {
		beginSaveRef.current = () => {
			savingRef.current = true;
			// Snapshot now: the host's PUT echo would otherwise move the diff baseline before
			// this tab's save starts.
			originalTestimonialsRef.current = agent?.publicBio?.testimonials ?? [];
		};
	}

	if (abortSaveRef) {
		abortSaveRef.current = () => {
			savingRef.current = false;
		};
	}

	// Expose bio dirtiness so a host (the detail-view refresh) can reflect it in a shared save bar.
	const computeDirty = (): boolean => {
		if (!isEnhancedBioEnabled) {
			return legacyState.head !== (agent.head || "") || legacyState.body !== (agent.body || "");
		}
		const orig = agent.publicBio;
		const norm = (v: unknown) => v ?? "";
		return (
			norm(publicBio.pageHeaderText) !== norm(orig?.pageHeaderText ?? agent.head) ||
			!!publicBio.showHeader !== !!orig?.showHeader ||
			norm(publicBio.heroImage?.url) !== norm(orig?.heroImage?.url) ||
			!!publicBio.quickSearch !== !!orig?.quickSearch ||
			norm(publicBio.logoImage?.url) !== norm(orig?.logoImage?.url) ||
			norm(publicBio.linkedinUrl) !== norm(orig?.linkedinUrl) ||
			norm(publicBio.facebookUrl) !== norm(orig?.facebookUrl) ||
			norm(publicBio.instagramUrl) !== norm(orig?.instagramUrl) ||
			norm(publicBio.twitterUrl) !== norm(orig?.twitterUrl) ||
			norm(publicBio.tiktokUrl) !== norm(orig?.tiktokUrl) ||
			norm(publicBio.aboutBody) !== norm(orig?.aboutBody ?? agent.body) ||
			(publicBio.markets ?? []).join(",") !== (orig?.markets ?? []).join(",") ||
			JSON.stringify(publicBio.testimonials ?? []) !== JSON.stringify(orig?.testimonials ?? [])
		);
	};
	if (dirtyRef) {
		dirtyRef.current = computeDirty;
	}
	useEffect(() => {
		if (onDirtyChange) {
			onDirtyChange();
		}
	}, [legacyState, publicBio, onDirtyChange]);

	const handleImageUpload = (field: 'heroImage' | 'logoImage', uploads: Upload[]) => {
		if (uploads.length > 0) {
			const upload = uploads[0];
			updatePublicBioField(field, { url: upload.url });
		}
	};

	const handleImageRemove = (field: 'heroImage' | 'logoImage') => {
		updatePublicBioField(field, null);
	};

	const openAddTestimonialModal = () => {
		setTestimonialFormData({ quote: '', author: '' });
		setEditingTestimonialIndex(null);
		setTestimonialModalOpen(true);
	};

	const openEditTestimonialModal = (index: number) => {
		const testimonial = publicBio.testimonials?.[index];
		if (testimonial) {
			setTestimonialFormData({ quote: testimonial.quote, author: testimonial.author });
			setEditingTestimonialIndex(index);
			setTestimonialModalOpen(true);
		}
	};

	const closeTestimonialModal = () => {
		setTestimonialModalOpen(false);
		setTestimonialFormData({ quote: '', author: '' });
		setEditingTestimonialIndex(null);
	};

	const saveTestimonial = () => {
		const newTestimonials = [...(publicBio.testimonials || [])];
		if (editingTestimonialIndex !== null) {
			// Edit existing — preserve the server id, or the save would treat this as a new
			// testimonial and create a duplicate instead of updating it.
			newTestimonials[editingTestimonialIndex] = {
				...newTestimonials[editingTestimonialIndex],
				...testimonialFormData,
			};
		} else {
			// Add new
			newTestimonials.push(testimonialFormData);
		}
		updatePublicBioField('testimonials', newTestimonials);
		closeTestimonialModal();
	};

	const deleteTestimonial = (index: number) => {
		setTestimonialToDelete(index);
		setDeleteConfirmOpen(true);
	};

	const confirmDeleteTestimonial = () => {
		if (testimonialToDelete !== null) {
			const newTestimonials = publicBio.testimonials?.filter((_, i) => i !== testimonialToDelete) || [];
			updatePublicBioField('testimonials', newTestimonials);
		}
		setDeleteConfirmOpen(false);
		setTestimonialToDelete(null);
	};

	const cancelDeleteTestimonial = () => {
		setDeleteConfirmOpen(false);
		setTestimonialToDelete(null);
	};

	const handleCancel = () => {
		setPublicBio({
			showHeader: agent.publicBio?.showHeader ?? false,
			pageHeaderText: agent.publicBio?.pageHeaderText ?? (agent.head || null),
			heroImage: agent.publicBio?.heroImage ?? null,
			quickSearch: agent.publicBio?.quickSearch ?? false,
			logoImage: agent.publicBio?.logoImage ?? null,
			linkedinUrl: agent.publicBio?.linkedinUrl ?? '',
			facebookUrl: agent.publicBio?.facebookUrl ?? '',
			instagramUrl: agent.publicBio?.instagramUrl ?? '',
			twitterUrl: agent.publicBio?.twitterUrl ?? '',
			tiktokUrl: agent.publicBio?.tiktokUrl ?? '',
			aboutBody: agent.publicBio?.aboutBody ?? (agent.body || null),
			markets: agent.publicBio?.markets ?? [],
			testimonials: agent.publicBio?.testimonials ?? [],
		});
		// The non-Enhanced branch edits legacyState, not publicBio — reset both so Cancel works
		// whichever branch is on screen.
		setLegacyState({
			head: agent.head || "",
			body: agent.body || "",
		});
		setErrors({});
		setSaveError(null);
	};

	if (cancelRef) {
		cancelRef.current = handleCancel;
	}

	if (!isEnhancedBioEnabled) {
		return (
			<Mui.Grid container spacing={1}>
				<Mui.Grid container alignItems="center" justifyContent="center" spacing={2}>
					<Mui.Grid item xs={12}>
						<Mui.Typography>Page Header</Mui.Typography>
					</Mui.Grid>
					<Mui.Grid item xs={12}>
						<RichTextEditor
							onChange={(text) => setLegacyState((prev) => ({ ...prev, head: text }))}
							toolbarId="head"
							value={legacyState.head}
						/>
					</Mui.Grid>
					<Mui.Grid item xs={12}>
						<Mui.Typography>Body Text</Mui.Typography>
					</Mui.Grid>
					<Mui.Grid item xs={12}>
						<RichTextEditor
							onChange={(text) => setLegacyState((prev) => ({ ...prev, body: text }))}
							toolbarId="body"
							value={legacyState.body}
						/>
					</Mui.Grid>
					{!hideSaveBar && (
						<Mui.Grid item xs={12}>
							<SaveButton
								loading={loading || false}
								snackBar
								label={{
									primary: "Save",
									inProgress: "Saving...",
									completed: "Saved"
								}}
								onClick={handleSaveLegacy}
							/>
						</Mui.Grid>
					)}
				</Mui.Grid>
			</Mui.Grid>
		);
	}

	return (
		<Mui.Grid container spacing={3}>
			<HeaderSection
				publicBio={publicBio}
				errors={errors}
				onUpdateField={updatePublicBioField}
				onImageUpload={handleImageUpload}
				onImageRemove={handleImageRemove}
			/>

			<Mui.Grid item xs={12}>
				<Mui.Divider />
			</Mui.Grid>

			<SocialMediaLinksSection
				publicBio={publicBio}
				errors={errors}
				onUpdateField={updatePublicBioField}
			/>

			<Mui.Grid item xs={12}>
				<Mui.Divider />
			</Mui.Grid>

			<AboutSection
				publicBio={publicBio}
				errors={errors}
				onUpdateField={updatePublicBioField}
			/>

			<Mui.Grid item xs={12}>
				<Mui.Divider />
			</Mui.Grid>

			<MarketsSection
				publicBio={publicBio}
				markets={markets}
				onUpdateField={updatePublicBioField}
			/>

			<Mui.Grid item xs={12}>
				<Mui.Divider />
			</Mui.Grid>

			<TestimonialsSection
				publicBio={publicBio}
				onAddTestimonial={openAddTestimonialModal}
				onDeleteTestimonial={deleteTestimonial}
				onEditTestimonial={openEditTestimonialModal}
			/>

			{/* Save Button */}
			<Mui.Grid item xs={12}>
				{saveError && (
					<Mui.Box mb={2}>
						<MuiAlert severity="error">{saveError}</MuiAlert>
					</Mui.Box>
				)}
				{!hideSaveBar && (
				<Mui.Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
				<Mui.Button
					variant="outlined"
					onClick={handleCancel}
					disabled={loading || false}
				>
					Cancel
				</Mui.Button>
				<Mui.Button
					variant="contained"
					color="primary"
					onClick={handleSaveOwnButton}
					disabled={loading || false}
				>
					{loading ? "Saving..." : "Save Changes"}
				</Mui.Button>
				</Mui.Box>
				)}
			</Mui.Grid>


		<Mui.Snackbar
		open={saveSuccess}
		autoHideDuration={4000}
		onClose={() => setSaveSuccess(false)}
		anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
	>
		<MuiAlert onClose={() => setSaveSuccess(false)} severity="success">
			Changes saved successfully.
		</MuiAlert>
	</Mui.Snackbar>

	<TestimonialModal
			open={testimonialModalOpen}
			testimonialData={testimonialFormData}
			onClose={closeTestimonialModal}
			onSave={saveTestimonial}
			onUpdateData={setTestimonialFormData}
		/>

		{/* Delete Confirmation Dialog */}
		<Mui.Dialog
			open={deleteConfirmOpen}
			onClose={cancelDeleteTestimonial}
		>
			<Mui.DialogTitle>Delete Testimonial</Mui.DialogTitle>
			<Mui.DialogContent>
				<Mui.Typography>
					Are you sure you want to delete this testimonial? This action cannot be undone.
				</Mui.Typography>
			</Mui.DialogContent>
			<Mui.DialogActions>
				<Mui.Button onClick={cancelDeleteTestimonial} color="primary" variant="outlined">
					Cancel
				</Mui.Button>
				<Mui.Button onClick={confirmDeleteTestimonial} color={"primary"} variant="contained">
					Delete
				</Mui.Button>
			</Mui.DialogActions>
		</Mui.Dialog>
		</Mui.Grid>
	);
};
